import { registerLocaleData } from '@angular/common';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeGr from '@angular/common/locales/el';
import { APP_INITIALIZER, inject, LOCALE_ID, NgModule, provideAppInitializer } from '@angular/core';
import {
  MatMomentDateModule,
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
} from '@angular/material-moment-adapter';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy } from '@angular/router';
import {
  IonicModule,
  IonicRouteStrategy,
  ModalController,
} from '@ionic/angular';
import { EffectsModule } from '@ngrx/effects';
import { Store, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CoreModule } from './core/models/core.module';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import * as StoreActions from './store/actions/store.actions';
import * as fromApp from './store/app.reducer';
import { AddressSelectorEffects } from './store/effects/address-selector.effects';
import { AuthEffects } from './store/effects/auth.effects';
import { BenEffects } from './store/effects/ben.effects';
import { BuildingEffects } from './store/effects/building.effects';
import { CatalogEffects } from './store/effects/catalog.effects';
import { NgChartsModule } from 'ng2-charts';
import { DocEffects } from './store/effects/doc.effects';
import { LutTablesEffects } from './store/effects/lut-tables.effects';
import { ParcelEffects } from './store/effects/parcel.effects';
import { SettlementDeedEffects } from './store/effects/settlement-deed.effects';
import { SettlementDeedsEffects } from './store/effects/settlement-deeds.effects';
import { StoreEffects } from './store/effects/store.effects';
import * as StoreSelectors from './store/selectors/store.selector';
import { DialogService } from './services/dialog.service';
import { AdvancedSearchEffects } from './store/effects/adv-search.effects';
import { ReportEffects } from './store/effects/report.effects';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { KeyService } from './services/key.service';
import { Observable, throwError } from 'rxjs';
import { UserEffects } from './store/effects/user.effects';
import { RoleSelectorEffects } from './store/effects/role-selector.effects';
import { RoleEffects } from './store/effects/role.effects';
import { AuditUserEffects } from './store/effects/audit-user.effects';
import { AuditPropsSearchEffects } from './store/effects/audit-prop-search.effects';
import { PolitikiGiEffects } from './store/effects/politiki-gi.effects';
import { MisthoseisEffects } from './store/effects/misthoseis.effects';
import { E9EntosEffects } from './store/effects/e9Entos.effects';
import { E9EktosEffects } from './store/effects/e9Ektos.effects';
import { MatSortModule } from '@angular/material/sort';

import { AuthModule, LogLevel, OpenIdConfiguration } from 'angular-auth-oidc-client';
import { RightsEffects } from './store/effects/rights.effects';
import { FilesModalModule } from './core/components/files-modal/files-modal.module';


registerLocaleData(localeGr);

export const LanguageLoader = (http: HttpClient): TranslateHttpLoader => {
  return new TranslateHttpLoader(http, 'assets/i18n/', '.json');
};

export const initApplication =
  (store: Store<fromApp.AppState>, dialogService) => (): Promise<unknown> => {
    store
      .select(StoreSelectors.selectAuthStateLoadingError)
      .pipe(filter((x) => !!x))
      .subscribe(async (error) => {
        const alert = await dialogService.createSystemErrorAlert(error);
        await alert.present();
      });

    return new Promise((resolve) => {
      store.dispatch(StoreActions.startAppInit());
      store.dispatch(StoreActions.loadAuthState());
      store
        .select(StoreSelectors.selectAuthStateLoaded)
        .pipe(filter((x) => !!x))
        .subscribe(() => {
          store.dispatch(StoreActions.finishAppInit());
          resolve(true);
        });
    });
  };

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function configureJSON(
  httpClient: HttpClient,
  keyService: KeyService
): () => Promise<any> {
  const configUrl = 'assets/config/config.json';

  let keyCode;
  const setupAction$ = httpClient.get<any>(configUrl).pipe(
    switchMap((customConfig) => {
      environment.apiUrl = customConfig.apiUrl;
      environment.helpUrl = customConfig.helpUrl;
      if (customConfig.evrymapUrl != null) {
        environment.evrymapUrl = customConfig.evrymapUrl;
        keyCode = customConfig.keyCode;
      }

      // //CHECK KEY
      return httpClient
        .get<any>(environment.apiUrl + '/auth/keyCode/' + customConfig.keyCode)
        .pipe(
          map((data) => {
            if (!data?.codeOk) {
            }
          }),
          catchError((err) => {
            return null;
          })
        );
    })
  );

  return () =>
    setupAction$.toPromise().catch((error) => {
    });
}

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function getConfig(): OpenIdConfiguration {
  let config = null;
  try {
    config = require('./assets/config/config.json');
  }
  catch {
    config = require('../assets/config/config.json');
  }
  if (!config.isTaxis || !config.authority) {
    return null;
  }
  const conf = {
    authority: config.authority,
    redirectUrl: config.redirectUrl,
    postLogoutRedirectUri: config.postLogoutRedirectUri,
    clientId: config.clientId,
    scope: config.scope,
    responseType: 'code',
    silentRenew: false,
    useRefreshToken: true,
    ignoreNonceAfterRefresh: true
  };

  return conf;
}


@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    CoreModule,
    NgChartsModule,
    BrowserModule,
    IonicModule.forRoot({
      innerHTMLTemplatesEnabled: true
    }),
    AppRoutingModule,
    BrowserAnimationsModule,
    StoreModule.forRoot(fromApp.appReducer),
    MatDatepickerModule,
    MatMomentDateModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    MatSortModule,
    FilesModalModule,
    AuthModule.forRoot({
      config: getConfig()
    }),
    EffectsModule.forRoot([
      StoreEffects,
      AuthEffects,
      CatalogEffects,
      SettlementDeedsEffects,
      SettlementDeedEffects,
      ParcelEffects,
      DocEffects,
      AddressSelectorEffects,
      PolitikiGiEffects,
      MisthoseisEffects,
      BenEffects,
      BuildingEffects,
      LutTablesEffects,
      AdvancedSearchEffects,
      ReportEffects,
      UserEffects,
      RoleSelectorEffects,
      RoleEffects,
      AuditUserEffects,
      AuditPropsSearchEffects,
      ReactiveFormsModule,
      E9EntosEffects,
      E9EktosEffects,
      RightsEffects
    ]),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: LanguageLoader,
        deps: [HttpClient],
      },
    }),
    StoreDevtoolsModule.instrument({ logOnly: environment.production, connectInZone: true })], providers: [
      provideAppInitializer(() =>
        configureJSON(inject(HttpClient), inject(KeyService))()
      ),

      provideAppInitializer(() =>
        initApplication(inject(Store<fromApp.AppState>), inject(DialogService))()
      ),
      { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
      { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { strict: true } },
      { provide: LOCALE_ID, useValue: 'el' },
      { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
      provideHttpClient(withInterceptorsFromDi()),
    ]
})
export class AppModule {
}
